import { EditorChangeType } from '../types';
import { AbstractCommand } from './AbstractCommand';

import type { IVideoEditor } from '../interface/IVideoEditor';
import type { VideoEditor } from '../VideoEditor';

export interface VideoTemplateInfo {
  id: string;
  elementIds: string[];
}

export interface AddVideoTemplateCommandOptions {
  newTemplate: VideoTemplateInfo;
  previousTemplate?: VideoTemplateInfo;
  createUndoEntry?: boolean;
}

/**
 * Command for adding or replacing a video template
 * Handles multiple elements (intro card, end card, logo) as a single undo/redo operation
 */
export class AddVideoTemplateCommand extends AbstractCommand {
  private options: AddVideoTemplateCommandOptions;
  private readonly _shouldCreateUndoRedoEntry: boolean;

  constructor(
    editor: VideoEditor | IVideoEditor,
    options: AddVideoTemplateCommandOptions,
  ) {
    super(editor as VideoEditor);
    this.options = options;
    this._shouldCreateUndoRedoEntry = options.createUndoEntry ?? true;
  }

  override shouldCreateUndoRedoEntry(): boolean {
    return this._shouldCreateUndoRedoEntry;
  }

  getName(): string {
    return 'AddVideoTemplateCommand';
  }

  execute(): boolean {
    // Capture timeline snapshot before making changes
    // This automatically handles undo/redo for all the elements
    super.execute();

    // Elements are already added to the timeline by the service
    // This command just manages the undo/redo behavior

    this.editor.notifyObservers(EditorChangeType.TIMELINE_CHANGED);
    return true;
  }

  undo(): boolean {
    // Use the default timeline snapshot restoration
    const success = super.undo();

    if (success) {
      this.editor.notifyObservers(EditorChangeType.TIMELINE_CHANGED);
    }

    return success;
  }

  getDescription(): string {
    const { previousTemplate } = this.options;

    if (previousTemplate) {
      return `replace_video_template`;
    } else {
      return `add_video_template`;
    }
  }
}
